import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { verifyToken, logoutAction, AUTH_TOKEN_KEY } from "../actions/auth";
import { setActiveAssistant } from "../actions/assistants";
import {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../actions/notifications";
import {
  AlertTriangle,
  CheckCircle2,
  CalendarX,
  Gauge,
  BadgeCheck,
  TrendingUp,
  Undo2,
  MessageSquare,
  Clock,
  Bot,
  Headset,
} from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";
import {
  BellIcon,
  ChevronDownIcon,
  GearIcon,
} from "../Pages/Dashboard/icons";
import { RoutesPaths } from "../Router/constant";
import { DocIcon } from "../Pages/Auth/onboarding/icons";

// Billing notifications are about the account rather than a conversation, so
// they carry no ids to deep-link with - the Billing page is where every one of
// them is acted on. The icon and tone are presentation; the API sends only the
// type, the title and the body.
// The API sends the type, the title and the body; the icon and the colour are
// decided here. A type this build has not met falls back to the plain unread
// dot rather than disappearing.
const NOTIF_META = {
  // Billing.
  subscription_activated: { icon: BadgeCheck, tone: "ok", billing: true },
  plan_upgraded: { icon: TrendingUp, tone: "ok", billing: true },
  payment_received: { icon: CheckCircle2, tone: "ok", billing: true },
  payment_failed: { icon: AlertTriangle, tone: "danger", billing: true },
  payment_recovered: { icon: CheckCircle2, tone: "ok", billing: true },
  payment_refunded: { icon: Undo2, tone: "warn", billing: true },
  subscription_cancelled: { icon: CalendarX, tone: "warn", billing: true },
  subscription_ended: { icon: CalendarX, tone: "danger", billing: true },

  // Conversations. These carry both ids and open on the conversation itself.
  new_conversation: { icon: MessageSquare, tone: "info" },
  visitor_waiting: { icon: Clock, tone: "warn" },
  // The assistant has handed a visitor to a person - the one notification
  // here that somebody is waiting on a reply.
  handoff_requested: { icon: Headset, tone: "danger" },

  // The assistant itself.
  assistant_ready: { icon: Bot, tone: "ok" },
  usage_limit: { icon: Gauge, tone: "warn", billing: true },
};

// An assistant notification with no conversation behind it: where to open it.
// The default is the dashboard; a first crawl finishing is better answered by
// the assistant's own page, which is where it can be tried out.
const ASSISTANT_ROUTE = {
  assistant_ready: RoutesPaths.ASSISTANT,
};

// Which tab on the Billing page answers it: a charge or a refund is a receipt,
// everything else is about the plan itself. The names match the tabs there.
const BILLING_TAB = {
  payment_received: "Invoices",
  payment_recovered: "Invoices",
  payment_refunded: "Invoices",
};

const LogOutIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <path
      d="M9 4.5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.5 16.5L20 12l-4.5-4.5M20 12H9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// One page of the bell feed. The endpoint is cursor-paginated, so pages are
// walked forward and remembered, rather than being addressable by number.
const NOTIFICATIONS_PER_PAGE = 10;

// "2 min ago" from an ISO timestamp - the payload carries createdAt, not a
// pre-formatted string.
const timeAgo = (value) => {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.round(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;

  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
};

function NotificationMenu() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  // A cursor feed, not numbered pages: the server sends nextCursor and null
  // when the feed ends. There is no total, and numbering would drift anyway
  // as new notifications insert at the top.
  const [cursor, setCursor] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const menuRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // The feed is per assistant, so switching in the sidebar switches this too.
  const assistantId = useSelector((state) => state.assistants.activeId);

  const refreshCount = useCallback(async () => {
    const result = await dispatch(getUnreadCount({ assistantId }));
    if (result?.success) setUnreadCount(result.count);
  }, [dispatch, assistantId]);

  // The badge is the only part that matters while the menu is shut, so the
  // feed itself is not fetched until it opens.
  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, 60000);
    return () => clearInterval(timer);
  }, [refreshCount]);

  const loadFeed = useCallback(
    async (from) => {
      setLoading(true);
      setError("");

      const result = await dispatch(
        listNotifications({
          cursor: from,
          limit: NOTIFICATIONS_PER_PAGE,
          assistantId,
        }),
      );

      setLoading(false);

      if (!result?.success) {
        setError(result?.message || "We couldn't load your notifications.");
        return;
      }

      // Older ones are appended; the first page replaces whatever was there.
      setItems((prev) =>
        from ? [...prev, ...result.notifications] : result.notifications,
      );
      setCursor(result.nextCursor);
    },
    [dispatch, assistantId],
  );

  // A different assistant is a different feed: anything already loaded belongs
  // to the one that was open before, so it is dropped rather than left on
  // screen under the new assistant's name.
  useEffect(() => {
    setItems([]);
    setCursor(null);
    setOpen(false);
  }, [assistantId]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const toggle = () => {
    setOpen((wasOpen) => {
      if (wasOpen) return false;

      // Always reopen at the newest, not wherever the last visit ended.
      setCursor(null);
      loadFeed(null);
      return true;
    });
  };

  const handleOpenItem = async (item) => {
    setOpen(false);

    if (!item.readAt) {
      setItems((prev) =>
        prev.map((n) =>
          n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      await dispatch(markNotificationRead(item.id));
      refreshCount();
    }

    // Conversation notifications carry both ids, which is exactly the deep
    // link the conversations page reads.
    if (item.conversationId) {
      const params = new URLSearchParams({
        conversationId: item.conversationId,
      });
      if (item.assistantId) params.set("assistantId", item.assistantId);
      navigate(`${RoutesPaths.CONVERSATIONS}?${params.toString()}`);
      return;
    }

    if (NOTIF_META[item.type]?.billing) {
      // An allowance is per assistant, so the one it is about becomes the one
      // the app is pointed at - otherwise the usage card on that page reports
      // a different assistant's numbers.
      if (item.assistantId) dispatch(setActiveAssistant(item.assistantId));

      const tab = BILLING_TAB[item.type];

      navigate(
        tab
          ? `${RoutesPaths.BILLING}?tab=${encodeURIComponent(tab)}`
          : RoutesPaths.BILLING,
      );
      return;
    }

    // Carries an assistant but nothing to open against it: point the app at
    // that assistant rather than whichever one was already selected, and open
    // the page that answers this kind of notification.
    if (item.assistantId) {
      dispatch(setActiveAssistant(item.assistantId));
      navigate(ASSISTANT_ROUTE[item.type] || RoutesPaths.DASHBOARD);
    }
  };

  const handleReadAll = async () => {
    setItems((prev) =>
      prev.map((n) =>
        n.readAt ? n : { ...n, readAt: new Date().toISOString() },
      ),
    );
    setUnreadCount(0);
    await dispatch(markAllNotificationsRead());
    refreshCount();
  };

  return (
    <div className="dash-topbar__user-menu" ref={menuRef}>
      <button
        type="button"
        className="dash-topbar__icon-btn dash-topbar__bell"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={toggle}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="dash-topbar__badge">{unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="dash-topbar__user-dropdown dash-topbar__notif-dropdown">
          <div className="dash-topbar__notif-header">
            <strong>Notifications</strong>
            <span className="dash-topbar__notif-head-right">
              {/* The count was hidden behind the action: with unread items
                  the header showed only the button, so the number the badge
                  promised was nowhere in the panel. */}
              <span className="dash-topbar__notif-count">
                {unreadCount > 0 ? `${unreadCount} new` : "All read"}
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="dash-topbar__notif-readall"
                  onClick={handleReadAll}
                >
                  Mark all read
                </button>
              )}
            </span>
          </div>

          <div className="dash-topbar__user-dropdown-divider" />

          {loading && items.length === 0 && (
            <ul className="dash-topbar__notif-list">
              {[0, 1, 2].map((row) => (
                <li className="dash-topbar__notif-item" key={row}>
                  <div className="dash-topbar__notif-body">
                    <span className="skel acct-skel--label" />
                    <span className="skel acct-skel--desc" />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {!loading && error && (
            <p className="dash-topbar__notif-empty">{error}</p>
          )}

          {!loading && !error && items.length === 0 && (
            <p className="dash-topbar__notif-empty">Nothing here yet.</p>
          )}

          {!error && items.length > 0 && (
            <ul className="dash-topbar__notif-list">
              {items.map((item) => (
                <li
                  key={item.id}
                  className={`dash-topbar__notif-item${item.readAt ? "" : " dash-topbar__notif-item--unread"}`}
                >
                  <button
                    type="button"
                    className="dash-topbar__notif-hit"
                    onClick={() => handleOpenItem(item)}
                  >
                    {NOTIF_META[item.type] ? (
                      <span
                        className={`dash-topbar__notif-icon is-${NOTIF_META[item.type].tone}`}
                      >
                        {React.createElement(NOTIF_META[item.type].icon, {
                          size: 15,
                          strokeWidth: 2.1,
                        })}
                      </span>
                    ) : (
                      <span className="dash-topbar__notif-dot" />
                    )}
                    <div className="dash-topbar__notif-body">
                      <strong>{item.title}</strong>
                      <span className="dash-topbar__notif-text">
                        {item.body}
                      </span>
                      <span className="dash-topbar__notif-time">
                        {timeAgo(item.createdAt)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* A non-null cursor is the server saying there is more; null is
              the end of the feed. No totals exist to count pages with. */}
          {cursor && (
            <>
              <div className="dash-topbar__user-dropdown-divider" />
              <button
                type="button"
                className="dash-topbar__notif-more"
                onClick={() => loadFeed(cursor)}
                disabled={loading}
              >
                {loading ? "Loading…" : "Load older"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// The name is all we have to build an avatar from; fall back to the email.
const initialOf = (user) =>
  (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

function UserMenu() {
  const [open, setOpen] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // App.js loads the session on boot; this covers a mount where it hasn't
  // landed yet (or was skipped), so the topbar never shows a blank user.
  useEffect(() => {
    if (!user && localStorage.getItem(AUTH_TOKEN_KEY)) {
      dispatch(verifyToken());
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const askLogout = () => {
    setOpen(false);
    setConfirmingLogout(true);
  };

  const cancelLogout = useCallback(() => setConfirmingLogout(false), []);

  const handleLogout = async () => {
    setConfirmingLogout(false);
    // Without this the token survives and the login route bounces straight back.
    await dispatch(logoutAction());
    navigate(RoutesPaths.LOGIN, { replace: true });
  };

  const handleAccountSettings = () => {
    setOpen(false);
    navigate(RoutesPaths.ACCOUNT_SETTINGS);
  };

  const handleBilling = () => {
    setOpen(false);
    navigate(RoutesPaths.BILLING);
  };

  return (
    <div className="dash-topbar__user-menu" ref={menuRef}>
      <button
        type="button"
        className="dash-topbar__user"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Avatar user={user} />
        <span className="dash-topbar__user-text">
          <strong>{user?.name || "—"}</strong>
          <span>{user?.email || ""}</span>
        </span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div className="dash-topbar__user-dropdown">
          <div className="dash-topbar__user-dropdown-header">
            <Avatar user={user} large />
            <div>
              <strong>{user?.name || "—"}</strong>
              <span>{user?.email || ""}</span>
            </div>
          </div>

          <div className="dash-topbar__user-dropdown-divider" />

          <button
            type="button"
            className="dash-topbar__user-dropdown-item"
            onClick={handleAccountSettings}
          >
            <GearIcon />
            Account settings
          </button>

          <button
            type="button"
            className="dash-topbar__user-dropdown-item"
            onClick={handleBilling}
          >
            <DocIcon />
            Billing
          </button>

          <div className="dash-topbar__user-dropdown-divider" />

          <button
            type="button"
            className="dash-topbar__user-dropdown-item dash-topbar__user-dropdown-item--danger"
            onClick={askLogout}
          >
            <LogOutIcon />
            Log out
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmingLogout}
        icon={<LogOutIcon />}
        title="Log out of Hostor?"
        message="You'll need to sign in again to get back to your dashboard."
        confirmLabel="Log out"
        cancelLabel="Stay signed in"
        onConfirm={handleLogout}
        onCancel={cancelLogout}
      />
    </div>
  );
}

function Avatar({ user, large = false }) {
  const className = `dash-topbar__avatar${large ? " dash-topbar__avatar--lg" : ""}`;

  if (user?.avatarUrl) {
    return (
      <img
        className={className}
        src={user.avatarUrl}
        alt=""
        referrerPolicy="no-referrer"
      />
    );
  }

  return <span className={className}>{initialOf(user)}</span>;
}

const defaultActions = (
  <>
    <NotificationMenu />
    <UserMenu />
  </>
);

export default function Topbar({
  title = "Overview",
  subtitle = "Here's how your assistant is performing 👋",
  actions,
  children,
}) {
  return (
    <div className="dash-topbar">
      <div className="dash-topbar__row">
        <div>
          <h1 className="dash-topbar__title">{title}</h1>
          <p className="dash-topbar__subtitle">{subtitle}</p>
        </div>

        <div className="dash-topbar__actions">{actions || defaultActions}</div>
      </div>

      {children && (
        <div className="dash-topbar__row dash-topbar__row--controls">
          {children}
        </div>
      )}
    </div>
  );
}
